import { createContext, useContext, useRef, type ReactNode } from 'react'
import { CompanionManager } from '../core/CompanionManager'
import type { CompanionConfig } from '../types/companion'

// ── Context ──────────────────────────────────────────────────

interface CompanionContextValue {
  manager: CompanionManager
}

const CompanionContext = createContext<CompanionContextValue | null>(null)

// ── Provider ─────────────────────────────────────────────────

interface CompanionProviderProps {
  config: CompanionConfig
  children: ReactNode
}

export function CompanionProvider({ config, children }: CompanionProviderProps) {
  const managerRef = useRef<CompanionManager | null>(null)

  if (!managerRef.current) {
    managerRef.current = new CompanionManager(config)
  }

  // Keep config in sync
  managerRef.current.updateConfig(config)

  return (
    <CompanionContext.Provider value={{ manager: managerRef.current }}>
      {children}
    </CompanionContext.Provider>
  )
}

// ── Hook to access manager ───────────────────────────────────

export function useCompanionManager(): CompanionManager {
  const ctx = useContext(CompanionContext)
  if (!ctx) {
    throw new Error('useCompanionManager must be used within CompanionProvider')
  }
  return ctx.manager
}
